import CmsBaseClass from "@/helpers/cms/CmsBaseClass.js";
import {tr} from "@vue-components/helpers/I18nPluginHelper.js";
import store from "@/store.js"
import Fuse from "fuse.js";

const FUSE_SEARCH_OPTIONS = {
    includeScore: true,
    shouldSort: true,
    threshold: 0.4,
    location: 0,
    distance: 1000,
    maxPatternLength: 64,
    minMatchCharLength: 2,
    keys: [`searchValue`]
};

const GENERIC_SEARCH_WORDS = [];

export const SEARCH_FUZZY_THRESHOLDS = [0, 0.2, 0.4];

const MAP_ITEM_TYPE_KEYWORDS = {
    "parking": {
        fr: `voiture, place, stationnement`,
        en: `car, parking, spot, space`
    },
    "zone-parking": {
        fr: `voiture, parking, stationnement, place`,
        en: `car, parking, spot, space`
    },
    "toilet": {
        fr: `toilette, toilettes, wc, salle de bain`,
        en: `toilet, wc, restroom, bathroom`
    },
    "elevator": {
        fr: `ascenseur`,
        en: `elevator, lift`
    },
    "escalator": {
        fr: `escalator, escalier mecanique`,
        en: `escalator`
    },
    "atm": {
        fr: `atm, gab, dab, distributeur de billet, guichet automatique`,
        en: `atm, cash machine, cash dispenser`
    },
    "store": {
        fr: `magasin, boutique, commerce`,
        en: `store, shop, boutique`
    },
    "restaurant": {
        fr: `restaurant, cafe, bar, cantine, self`,
        en: `restaurant, cafe, bar, canteen`
    },
    "zone-food": {
        fr: `restaurant, cafe, bar, cantine, self, food court`,
        en: `restaurant, cafe, bar, canteen, food court`
    },
    "security": {
        fr: `securite, securite, police, gendarmerie, pompiers, urgence`,
        en: `security, police, firefighters, emergency`
    },
    "metro": {
        fr: `metro, station, transports`,
        en: `metro, station, transports`
    }
};

function normalizeSearchWords(value) {
    // The query and CMS values share this normalization: "Entrée Pavill" becomes ["entree", "pavill"].
    return value.toString().normalize(`NFKD`).replace(/[\u0300-\u036f]/g, ``).toLowerCase().split(/\W+/).filter(Boolean);
}

export function createSearchRegexp(value) {
    const words = normalizeSearchWords(value);
    // The compact form also ignores punctuation: "C-RC-00046" can be searched as "CRC00046".
    const searchRegexp = new RegExp(words.join(``), `i`);
    searchRegexp.search = words.join(``);
    searchRegexp.words = words;
    searchRegexp.query = words.join(` `);
    searchRegexp.fuzzyWords = words.filter(word => !GENERIC_SEARCH_WORDS.includes(word));
    if (searchRegexp.fuzzyWords.length === 0) {
        searchRegexp.fuzzyWords = words;
    }
    searchRegexp.fuzzyThreshold = SEARCH_FUZZY_THRESHOLDS[0];
    return searchRegexp;
}

export default class CmsItem extends CmsBaseClass {
    get ignoredFields() {
        return [`mapItems`, `typeDef`];
    }

    /**
     *
     * @param data {Object}
     * @param typeDef {CmsType}
     */
    constructor(data, typeDef) {
        super(data);

        /**
         *
         * @type {CmsType}
         */
        this.typeDef = typeDef;
        if (!this.typeDef) {
            console.error(`Invalid item type for "${tr(this.name)}" (ID: ${this.id}, Type: ${this.type || `undefined`})`);
        } else {
            if (typeDef.displayId) {
                this.name = `${this.id} - ${tr(this.name)}`;
            }
        }
    }

    matchSearch(searchRegexp) {
        return this.getSearchMatches(searchRegexp).length > 0;
    }

    getSearchScore(searchRegexp) {
        const matches = this.getSearchMatches(searchRegexp);
        if (matches.length === 0) {
            return null;
        }
        let score = 0;
        for (let match of matches) {
            if (match.score > score) {
                score = match.score;
            }
        }
        return score + this.priority + this.typeDef.priority;
    }

    getSearchMatches(searchRegexp) {
        // Filtering, ranking, keyword badges and console match reasons all use this same match list.
        const matches = [];
        for (let field of this.typeDef.getSearchableFields()) {
            let value = this.getField(field.id);
            if (!value) {
                continue;
            }

            let values = typeof value === `object` ? Object.values(value) : [value];
            if (field.type === `keywords` || field.id.toLowerCase().endsWith(`keywords`)) {
                // Match each keyword independently. "entrée principale" and "Pavillon Enfant Soleil"
                // must not combine to make Urgences match the query "Entrée Pavill".
                const keywords = [];
                for (let fieldValue of values) {
                    if (!fieldValue) {
                        continue;
                    }
                    const fieldKeywords = Array.isArray(fieldValue) ? fieldValue : fieldValue.split(`,`);
                    for (let keyword of fieldKeywords) {
                        keywords.push(keyword.trim());
                    }
                }
                values = keywords;
            }

            for (let fieldValue of values) {
                const score = this.getSearchValueScore(searchRegexp, fieldValue, field);
                if (score) {
                    matches.push({field, value: fieldValue, score});
                }
            }
        }

        if (this.mapItems) {
            // Map point types provide implicit keywords, such as "toilette" for a toilet point.
            const field = {id: `keyword`, type: `keywords`};
            for (let mapItem of this.mapItems) {
                const keywords = MAP_ITEM_TYPE_KEYWORDS[mapItem.type];
                if (!keywords) {
                    continue;
                }
                for (let translation of Object.values(keywords)) {
                    for (let keyword of translation.split(`,`)) {
                        const value = keyword.trim();
                        const score = this.getSearchValueScore(searchRegexp, value, field);
                        if (score) {
                            matches.push({field, value, score});
                        }
                    }
                }
            }
        }
        return matches;
    }

    getSearchValueScore(searchRegexp, value, field) {
        if (!value) {
            return 0;
        }

        // Address fields may hold a room spec (ex: "7504-7543, !7510, !7520-7522"); the spec then decides room queries.
        if (field.type === `address`) {
            const roomSpecScore = this.getRoomSpecScore(searchRegexp, value);
            if (roomSpecScore !== null) {
                return roomSpecScore;
            }
        }

        const valueWords = normalizeSearchWords(value);
        const searchValues = valueWords.map(searchValue => ({searchValue}));
        searchValues.push({searchValue: valueWords.join(``)});
        const fuse = new Fuse(searchValues, FUSE_SEARCH_OPTIONS);
        let fuseScore = 0;

        for (let searchWord of searchRegexp.fuzzyWords) {
            let bestResult;
            // Prefixes must score zero in every field, otherwise an exact name match
            // stops the search before a partial keyword can be included.
            const prefixMatch = searchValues.find(searchValue => searchWord.length >= 3 || field.id === `name` ? searchValue.searchValue.startsWith(searchWord) : searchValue.searchValue === searchWord);
            if (prefixMatch) {
                bestResult = {score: 0, item: prefixMatch};
            } else if (searchWord.length >= 3) {
                // Apply the same typo tolerance to names and keywords.
                bestResult = fuse.search(searchWord)[0];
                for (let index = 0; index < searchWord.length - 1; index++) {
                    const characters = searchWord.split(``);
                    const character = characters[index];
                    characters[index] = characters[index + 1];
                    characters[index + 1] = character;
                    const transposedWord = characters.join(``);
                    if (valueWords.includes(transposedWord)) {
                        bestResult = {score: 0.05, item: {searchValue: transposedWord}};
                        break;
                    }
                }
            }
            if (!bestResult || bestResult.score > searchRegexp.fuzzyThreshold) {
                return 0;
            }
            if (searchRegexp.fuzzyThreshold > 0.2) {
                const prefixLength = searchWord.length < 6 ? 2 : 3;
                if (bestResult.item.searchValue.slice(0, prefixLength) !== searchWord.slice(0, prefixLength)) {
                    return 0;
                }
            }
            fuseScore += bestResult.score;
        }

        let score = Math.round((1 - (fuseScore / searchRegexp.fuzzyWords.length)) * 10);
        if (field.id === `name`) {
            score += 2;
            if (valueWords.join(``).includes(searchRegexp.search)) {
                score += 2;
            }
        }
        return score;
    }

    /**
     * Scores a room query against a room-spec address such as "7504-7543, !7510, !7520-7522" or "7500a-7550c".
     * A room matches when it matches at least one inclusion term and no exclusion term; exclusions always win,
     * regardless of their position in the list, so editors can append terms in any order.
     * Returns null when the value is not a room spec or the query is not a room number, so the caller
     * falls back to plain text scoring (ex: address "123 Main Street", or query "cafeteria").
     */
    getRoomSpecScore(searchRegexp, value) {
        const terms = this.parseRoomSpec(value);
        if (!terms) {
            return null;
        }

        // searchRegexp.search is already compact ("7515 b" => "7515b").
        const room = this.parseRoom(searchRegexp.search);
        if (!room) {
            return null;
        }

        // A query with more digits than any room in the spec is not a room lookup (ex: "75047543"
        // from typing the spec itself literally); let plain text scoring handle it.
        const maxDigits = Math.max(...terms.map(term => Math.max(`${term.start.number}`.length, `${term.end.number}`.length)));
        if (`${room.number}`.length > maxDigits) {
            return null;
        }

        // Exclusions always win. Example: "7504-7543, !7510" => query "7510" scores 0.
        for (const term of terms) {
            if (term.excluded && this.isRoomInTerm(room, term)) {
                return 0;
            }
        }
        // In-spec room ranks like an exact text match. Example: "7515" in "7504-7543" => 10.
        for (const term of terms) {
            if (!term.excluded && this.isRoomInTerm(room, term)) {
                return 10;
            }
        }
        // Neighbor tolerance: same room number with a different letter suffix still matches.
        // Example: query "7515" finds an item that only lists "7515b" (and vice versa).
        for (const term of terms) {
            if (!term.excluded && room.number >= term.start.number && room.number <= term.end.number) {
                return 9;
            }
        }
        return 0;
    }

    /**
     * Parses a room-spec address into terms. Example: "7504-7543, !7510" =>
     * [{excluded: false, start: {number: 7504, suffix: ""}, end: {number: 7543, suffix: ""}},
     *  {excluded: true, start: {number: 7510, suffix: ""}, end: {number: 7510, suffix: ""}}].
     * Returns null when any part does not parse (the value is a normal address, not a spec).
     */
    parseRoomSpec(value) {
        if (typeof value !== `string` && typeof value !== `number`) {
            return null;
        }

        const terms = [];
        for (let part of value.toString().split(`,`)) {
            part = part.trim();
            if (!part) { // Tolerate trailing/double commas.
                continue;
            }

            let excluded = false;
            if (part.startsWith(`!`)) {
                excluded = true;
                part = part.slice(1).trim();
            }

            const bounds = part.split(/\s*[-–]\s*/);
            if (bounds.length > 2) {
                return null;
            }
            const start = this.parseRoom(bounds[0]);
            const end = bounds.length === 2 ? this.parseRoom(bounds[1]) : start;
            if (!start || !end) {
                return null;
            }
            terms.push({excluded, start, end});
        }
        return terms.length > 0 ? terms : null;
    }

    parseRoom(text) {
        // Example: "7515B" => {number: 7515, suffix: "b"}; "7515" => {number: 7515, suffix: ""}.
        const match = /^(\d+)\s*([a-z]{0,3})$/.exec((text ?? ``).toString().trim().toLowerCase());
        if (!match) {
            return null;
        }
        return {number: parseInt(match[1], 10), suffix: match[2]};
    }

    isRoomInTerm(room, term) {
        if (room.number < term.start.number || room.number > term.end.number) {
            return false;
        }
        // A bound without a suffix is inclusive of every suffix at that number ("7504-7543" contains "7543b"),
        // while a suffixed bound compares suffixes alphabetically ("7500a-7550c" excludes "7550d").
        if (room.number === term.start.number && room.suffix < term.start.suffix) {
            return false;
        }
        if (room.number === term.end.number && term.end.suffix && room.suffix > term.end.suffix) {
            return false;
        }
        return true;
    }

    matchSearchName(searchRegexp) {
        for (let match of this.getSearchMatches(searchRegexp)) {
            if (match.field.id === `name`) {
                return true;
            }
        }
        return false;
    }

    getMatchingKeywords(searchRegexp, asString = true) {
        const keywords = [];
        for (let match of this.getSearchMatches(searchRegexp)) {
            const value = match.value.toString();
            if (match.field.id !== `name` && !keywords.includes(value)) {
                keywords.push(value);
            }
        }
        if (keywords.length === 0) {
            return null;
        }
        if (asString) {
            return keywords.join(`, `);
        } else {
            return keywords;
        }
    }

    getHighlightedMatchingKeywords(searchRegexp) {
        const keywords = this.getMatchingKeywords(searchRegexp, false);
        if (!keywords) {
            return null;
        }
        return keywords.map(keyword => this.highlightSearchText(keyword, searchRegexp)).join(`, `);
    }

    highlightSearchText(text, searchRegexp) {
        let ranges = [];
        const compactText = this.getCompactTextParts(text);
        // Find compact query in compact text
        const compactIndex = compactText.value.indexOf(searchRegexp.search);
        if (compactIndex >= 0) {
            // Convert compact index back to original text indexes (ex: query "centre" => range {start: 0, end: 6})
            ranges.push({
                start: compactText.indexes[compactIndex],
                end: compactText.indexes[compactIndex + searchRegexp.search.length - 1] + 1
            });
        }
        // Find each query word separately
        for (let searchWord of searchRegexp.words) {
            const wordIndex = compactText.value.indexOf(searchWord);
            if (wordIndex >= 0) {
                // Convert word compact index to original text range (ex: "prelevement" => range covering the visible word)
                ranges.push({
                    start: compactText.indexes[wordIndex],
                    end: compactText.indexes[wordIndex + searchWord.length - 1] + 1
                });
            }
        }
        // ex output: "<mark>Centre</mark> de <mark>prelevement</mark>"
        return this.highlightSearchRanges(text, ranges);
    }

    getCompactTextParts(text) {
        // Converts text to compact searchable text while keeping original positions
        let value = ``;
        let indexes = [];
        for (let index = 0; index < text.length; index += 1) {
            // Remove accents, punctuation, and spaces (ex: "COVID-19 Test Centre" => "covid19testcentre")
            const normalizedChar = text[index].normalize(`NFKD`).replace(/[\u0300-\u036f]/g, ``).replace(/\W/g, ``).toLowerCase();
            if (normalizedChar) {
                value += normalizedChar;
                indexes.push(index);
            }
        }
        // output: { value: "covid19testcentre", indexes: [0, 1, 2, 3, 4, 6, 7, 9, 10, ...] }
        return {value, indexes};
    }

    highlightSearchRanges(text, ranges) {
        // Wraps matched original ranges in mark tags
        if (ranges.length === 0) {
            return this.escapeSearchHtml(text);
        }

        // Sort ranges in reading order (ex: "centre" range before "prelevement" range)
        ranges.sort((a, b) => a.start - b.start);
        let highlighted = ``;
        let index = 0;
        for (let range of ranges) {
            if (range.start < index) {
                // Skip overlapping ranges (ex: full compact match already covered "centre")
                continue;
            }
            // Add text before the match (ex: In "Centre de prelevement", add text before "prelevement" match which is "de")
            highlighted += this.escapeSearchHtml(text.slice(index, range.start));
            // Add matched text with highlight (ex: "Centre" => "<mark>Centre</mark>")
            highlighted += `<mark class="search-highlight">${this.escapeSearchHtml(text.slice(range.start, range.end))}</mark>`;
            index = range.end;
        }
        // Add remaining text after the last match
        highlighted += this.escapeSearchHtml(text.slice(index));
        return highlighted;
    }

    escapeSearchHtml(text) {
        // Escapes user/CMS text before injecting highlight markup. Example: "A & B" => "A &amp; B".
        return text.replace(/&/g, `&amp;`).replace(/</g, `&lt;`).replace(/>/g, `&gt;`).replace(/"/g, `&quot;`).replace(/'/g, `&#039;`);
    }

    matchPermalink(permalink) {
        if (this.permalink && typeof this.permalink === `string` && permalink === this.permalink) {
            return true;
        } else if (this.permalink && typeof this.permalink === `object` &&
            (permalink === this.permalink.en || permalink === this.permalink.default || permalink === this.permalink.fr)) {
            return true;
        }
        return permalink === this.id;
    }

    getName() {
        return this.name;
    }

    getField(field) {
        if (field === `secondaryField`) {
            return this.getSecondaryValue();
        } else if (field === `previewImage`) {
            return this.getPreviewImage();
        } else if (field === `mapImage`) {
            return this.mapItem?.image;
        } else if (field === `self-link`) {
            return this.getViewLink();
        } else if (field === `map-link`) {
            return this.getMapLink();
        } else if (field === `map-link-or-self-link`) {
            return this.getMapLink() || this.getViewLink();
        } else {
            return this.getNestedValue(field);
        }
    }

    getFieldFormatted(field) {
        let value = this.getField(field);
        if (!value) {
            return value;
        }

        if (typeof value === `object`) {
            return tr(value);
        } else {
            return value;
        }
    }

    addMapItem(mapItem) {
        if (!mapItem)
            return;

        if (!this.mapItems) {
            this.mapItems = [mapItem];
        } else if (this.mapItems.indexOf(mapItem) === -1) {
            this.mapItems.push(mapItem);
        }
        mapItem.setCmsItem(this);
    }

    hasMultipleMapItems() {
        return this.mapItems?.length > 1;
    }

    get mapItem() {
        if (this.mapItems && this.mapItems.length > 0) {
            return this.mapItems[0];
        } else {
            return null;
        }
    }

    getViewLink() {
        if (!this.typeDef.viewUrl) {
            return null;
        }
        let permalink = tr(this.permalink);
        if (!permalink) {
            permalink = this.id;
        }
        return this.typeDef.viewUrl.replace(`{id}`, permalink);
    }

    getMapLink() {
        if (!this.mapItem) {
            return null;
        }

        const mapPage = store.state.website.getMapPage();
        if (mapPage) {
            return `/${tr(mapPage.permalink)}?point=${this.mapItem.getPermalink()}`;
        } else {
            console.error(`Error, no map page detected`);
            return null;
        }
    }

    getPreviewImage() {
        return this.getNestedValue(this.typeDef.previewImage);
    }

    getSecondaryValue() {
        return this.getNestedValue(this.typeDef.secondaryField);
    }
}
